import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import MainCard from 'components/MainCard';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { CollectionStatus, CollectionType, DeliveryMemberCollectionHistoryType } from 'types';
import { checkNullable, getCollectionStatusChipStyle, parseCollectionStatus, parseCollectionType, separatePhoneBy2digits } from 'utils/utils';
import useStore from 'zustand-store';
import { format } from 'date-fns';
import { centsToDirhamsString } from 'utils';

const CollectionDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const isFetching = useStore(state => state.isFetching);
    const cancelDeliveryMemberCollection = useStore(state => state.cancelDeliveryMemberCollection);

    const [collectionDetails, setCollectionDetails] = useState<DeliveryMemberCollectionHistoryType>(location.state?.collectionDetails);
    const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);

    const deliveryMemberDetails = location.state?.deliveryMember;

    const handleConfirmCancel = async () => {
        if (!openCancelDialog) return;

        await cancelDeliveryMemberCollection(
            {
                deliveryAgentUuid: deliveryMemberDetails.uuid,
                invoiceId: collectionDetails.id,
            },
            (response) => {
                setOpenCancelDialog(false);
                setCollectionDetails({ ...collectionDetails, deliveryInvoiceStatus: CollectionStatus.CANCELED, cancelationDate: response.cancelationDate })
            }
        );
    };

    type InfoRow = { label: string; value: string };

    const InfoCard = ({ title, rows }: { title: string; rows: InfoRow[] }) => (
        <Stack spacing={1.5}>
            <Typography variant="h5" fontWeight={700}>{title}</Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2 }}>
                {rows.map((row, index) => (
                    <Box key={row.label}>
                        {index > 0 && <Divider />}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
                            <Typography color="textSecondary" sx={{ fontSize: 13 }}>{row.label}</Typography>
                            <Typography fontWeight={600}>{row.value}</Typography>
                        </Stack>
                    </Box>
                ))}
            </Box>
        </Stack>
    );

    const customerRows: InfoRow[] = [
        { label: "Prénom", value: checkNullable(collectionDetails.payerFirstName, "N/A") },
        { label: "Nom", value: checkNullable(collectionDetails.payerLastName, "N/A") },
        { label: "Téléphone", value: collectionDetails.payerPhoneNumber ? separatePhoneBy2digits(collectionDetails.payerPhoneNumber) : "N/A" },
    ];

    if (location.state === null ||
        location.state === undefined ||
        location.state.deliveryMember === null ||
        location.state.deliveryMember === undefined ||
        location.state.collectionDetails === null ||
        location.state.collectionDetails === undefined
    ) {
        return <Navigate replace to={'/delivery'} />
    }

    return (
        <Box>
            <MainCard border>
                <Box sx={{
                    paddingTop: "16px",
                    paddingBottom: "16px",
                    display: "flex",
                    flexDirection: "row",
                    width: '100%',
                    alignItems: "center"
                }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            borderWidth: '2px',
                            borderColor: "#E0E0E3",
                            borderRadius: "4px",
                            backgroundColor: "#F8F8F9",
                            padding: "12px 15px",
                        }}
                    >
                        <ArrowBackIosNewIcon sx={{ color: "#373737", fontSize: "12px" }} />
                    </IconButton>
                    <Box sx={{ marginLeft: '25px' }}>
                        <Stack spacing={0} flexDirection="row" alignSelf="center" alignItems="center" justifyContent="left">
                            <Typography variant='h5' sx={{ mr: 1 }}>
                                Détail encaissement
                            </Typography>
                            <Divider orientation="vertical" flexItem />
                            <Chip
                                style={getCollectionStatusChipStyle(collectionDetails.deliveryInvoiceStatus)}
                                label={parseCollectionStatus(collectionDetails.deliveryInvoiceStatus)}
                                size="small"
                                variant="light"
                                sx={{ ml: 1 }}
                            />
                        </Stack>
                    </Box>
                    <Box sx={{ display: "flex", justifySelf: "flex-end", marginLeft: "auto" }}>
                        {collectionDetails.deliveryInvoiceStatus === CollectionStatus.NOT_PAID &&
                            <Button
                                onClick={() => setOpenCancelDialog(true)}
                                variant="outlined"
                                sx={{
                                    color: "#CD1818",
                                    borderColor: "#CD1818",
                                    marginLeft: "5px",
                                    backgroundColor: "#fff",
                                    "&:hover": {
                                        borderColor: "#CD1818",
                                        backgroundColor: "rgba(205, 24, 24, 0.04)",
                                        color: "#CD1818",
                                    },
                                }}
                            >
                                Annuler
                            </Button>
                        }
                    </Box>
                </Box>
            </MainCard>
            <MainCard border title="Résumé" sx={{ marginTop: "24px" }}>
                <Stack direction="row" justifyContent="space-around" alignItems="center">
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Montant encaissé</Typography>
                        <Typography>{centsToDirhamsString(collectionDetails.amount)} Dhs</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Type</Typography>
                        <Typography>{parseCollectionType(collectionDetails.deliveryPaymentType)}</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Date d'encaissement</Typography>
                        <Typography whiteSpace={"pre"}>
                            {format(new Date(collectionDetails.creationDate), "dd/MM/yyyy") + " à " + format(new Date(collectionDetails.creationDate), "HH:mm")}
                        </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Référence facture / BL</Typography>
                        <Typography>{checkNullable(collectionDetails.reference, "N/A")}</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Reversement associé</Typography>
                        <Typography>{checkNullable(collectionDetails.transactionId, "N/A")}</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Statut</Typography>
                        <Typography>{parseCollectionStatus(collectionDetails.deliveryInvoiceStatus)}</Typography>
                    </Stack>
                </Stack>
            </MainCard>

            <MainCard border sx={{ marginTop: "24px" }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InfoCard
                            title="Livreur"
                            rows={[
                                { label: "Prénom", value: checkNullable(deliveryMemberDetails.firstName, "N/A") },
                                { label: "Nom", value: checkNullable(deliveryMemberDetails.lastName, "N/A") },
                                { label: "Téléphone", value: deliveryMemberDetails.phoneNumber ? separatePhoneBy2digits(deliveryMemberDetails.phoneNumber) : "N/A" },
                            ]}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InfoCard title="Client" rows={customerRows} />
                    </Grid>
                </Grid>
            </MainCard>

            <MainCard border title="Historique" sx={{ marginTop: "24px" }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>DATE</TableCell>
                            <TableCell>TRANSACTION ID</TableCell>
                            <TableCell>STATUT</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            collectionDetails.creationDate && <TableRow key={0}>
                                <TableCell>
                                    <Typography whiteSpace={"pre"}>{format(new Date(collectionDetails.creationDate), "dd/MM/yyyy") + " à " + format(new Date(collectionDetails.creationDate), "HH:mm:ss")}</Typography>
                                </TableCell>
                                <TableCell>N/A</TableCell>
                                <TableCell>{collectionDetails.deliveryPaymentType === CollectionType.CASH ? "À REVERSER" : collectionDetails.deliveryPaymentType === CollectionType.QR_CODE ? "GÉNÉRÉ" : "N/A"}</TableCell>
                            </TableRow>
                        }
                        {
                            collectionDetails.cancelationDate && <TableRow key={1}>
                                <TableCell>
                                    <Typography whiteSpace={"pre"}>{format(new Date(collectionDetails.cancelationDate), "dd/MM/yyyy") + " à " + format(new Date(collectionDetails.cancelationDate), "HH:mm:ss")}</Typography>
                                </TableCell>
                                <TableCell>N/A</TableCell>
                                <TableCell>ANNULÉ</TableCell>
                            </TableRow>
                        }
                        {
                            collectionDetails.paymentDate && <TableRow key={2}>
                                <TableCell>
                                    <Typography whiteSpace={"pre"}>{format(new Date(collectionDetails.paymentDate), "dd/MM/yyyy") + " à " + format(new Date(collectionDetails.paymentDate), "HH:mm:ss")}</Typography>
                                </TableCell>
                                <TableCell>{checkNullable(collectionDetails.transactionId, "N/A")}</TableCell>
                                <TableCell>{collectionDetails.deliveryPaymentType === CollectionType.CASH ? "REVERSÉ" : collectionDetails.deliveryPaymentType === CollectionType.QR_CODE ? "PAYÉ" : "N/A"}</TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </MainCard>
            <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
                <DialogTitle>Annuler cet encaissement ?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Le montant de cet encaissement sera déduit du montant à reverser et du cash en possession du livreur.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disableElevation disabled={isFetching} onClick={() => setOpenCancelDialog(false)}>
                        Retour
                    </Button>
                    <Button disableElevation disabled={isFetching} autoFocus onClick={handleConfirmCancel}>
                        Confirmer l’annulation
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CollectionDetails;